<template>
  <div class="partner-shell partner-page">
    <template v-if="!isApproved">
      <section class="intro">
        <div class="intro-copy">
          <span class="eyebrow">{{ $t('partner.eyebrow') }}</span>
          <h1 class="intro-title">{{ $t('partner.title') }}</h1>
          <p class="intro-lead">{{ $t('partner.subtitle') }}</p>
          <ul class="benefits">
            <li v-for="benefit in benefits" :key="benefit.key" class="benefit">
              <span class="benefit-icon material-icons-outlined" aria-hidden="true">{{ benefit.icon }}</span>
              <div>
                <strong>{{ benefit.title }}</strong>
                <span>{{ benefit.desc }}</span>
              </div>
            </li>
          </ul>
        </div>

        <div class="apply-card">
          <div v-if="!authResolved || (user && isLoading && !summary)" class="card-state card-loading">
            <i class="material-icons spinning">refresh</i>
          </div>

          <div v-else-if="!user" class="card-state">
            <h2 class="card-title">{{ $t('partner.form.title') }}</h2>
            <p class="card-text">{{ $t('partner.loginHint') }}</p>
            <button type="button" class="primary-btn wide-btn" @click="showLoginModal">{{ $t('partner.loginButton') }}</button>
          </div>

          <div v-else-if="loadError" class="card-state">
            <p class="form-error">{{ loadError }}</p>
            <button type="button" class="secondary-btn" @click="load">{{ $t('partner.retry') }}</button>
          </div>

          <div v-else-if="partner && partner.status === 'pending'" class="card-state">
            <span class="status-tag is-wait">{{ $t('partner.status.pending') }}</span>
            <h2 class="card-title">{{ $t('partner.pendingTitle') }}</h2>
            <p class="card-text">{{ $t('partner.pendingDesc', { date: formatDate(partner.appliedAt) }) }}</p>
          </div>

          <form v-else-if="summary" class="apply-form" @submit.prevent="submitApplication">
            <h2 class="card-title">{{ $t('partner.form.title') }}</h2>
            <p v-if="partner && partner.status === 'rejected'" class="card-text">
              <span class="status-tag is-bad">{{ $t('partner.status.rejected') }}</span>
              {{ partner.reviewNote || $t('partner.rejectedDesc') }}
            </p>
            <label v-for="field in applicationFields" :key="field.key" class="form-field">
              <span class="field-label">{{ field.label }}</span>
              <input
                v-model.trim="form[field.key]"
                class="text-input"
                :placeholder="field.placeholder"
                required
                maxlength="500"
              >
            </label>
            <span v-if="formError" class="form-error">{{ formError }}</span>
            <button type="submit" class="primary-btn wide-btn" :disabled="isSubmitting">{{ $t('partner.form.submit') }}</button>
            <span class="card-note">{{ $t('partner.form.note') }}</span>
          </form>
        </div>
      </section>

      <section class="steps-section">
        <h2 class="section-title">{{ $t('partner.stepsTitle') }}</h2>
        <ol class="steps">
          <li v-for="(step, index) in steps" :key="step.title" class="step">
            <span class="step-index">{{ String(index + 1).padStart(2, '0') }}</span>
            <strong>{{ step.title }}</strong>
            <span>{{ step.desc }}</span>
          </li>
        </ol>
      </section>
    </template>

    <template v-else>
      <header class="workspace-header">
        <div class="workspace-heading">
          <h1 class="workspace-title">{{ $t('partner.centerTitle') }}</h1>
          <p class="workspace-subtitle">{{ $t('partner.centerSubtitle') }}</p>
        </div>
      </header>

      <p v-if="partner.status === 'disabled'" class="workspace-panel status-panel">
        <span class="status-tag is-bad">{{ $t('partner.status.disabled') }}</span>
        {{ $t('partner.disabledDesc') }}
      </p>

      <section class="workspace-panel share-panel">
        <div class="share-block">
          <span class="field-label">{{ $t('partner.codeLabel') }}</span>
          <div class="control-row">
            <strong class="partner-code">{{ summary.code }}</strong>
            <button type="button" class="secondary-btn" @click="copy(summary.code)">
              <span class="material-icons-outlined">content_copy</span>
              {{ $t('partner.copyCode') }}
            </button>
          </div>
        </div>
        <div class="share-block">
          <span class="field-label">{{ $t('partner.linkLabel') }}</span>
          <div class="control-row">
            <input class="text-input link-input" :value="referralLink" readonly :aria-label="$t('partner.linkLabel')" @focus="$event.target.select()">
            <button type="button" class="primary-btn" @click="copy(referralLink)">
              <span class="material-icons-outlined">link</span>
              {{ $t('partner.copyLink') }}
            </button>
          </div>
        </div>
        <p v-if="partner.premium" class="share-note">{{ premiumLine }}</p>
      </section>

      <div class="stat-row">
        <div class="stat">
          <span class="field-label">{{ $t('partner.stats.invitees') }}</span>
          <strong>{{ records.totals.invitees }}</strong>
        </div>
        <div class="stat">
          <span class="field-label">{{ $t('partner.stats.paidInvitees') }}</span>
          <strong>{{ records.totals.paidInvitees }}</strong>
        </div>
        <div class="stat">
          <span class="field-label">{{ $t('partner.stats.pending') }}</span>
          <strong>{{ money(records.totals.pending) }}</strong>
        </div>
        <div class="stat">
          <span class="field-label">{{ $t('partner.stats.available') }}</span>
          <strong>{{ money(records.totals.available) }}</strong>
        </div>
        <div class="stat">
          <span class="field-label">{{ $t('partner.stats.paid') }}</span>
          <strong>{{ money(records.totals.paid) }}</strong>
        </div>
      </div>

      <section>
        <h2 class="section-title">{{ $t('partner.invitees.title') }}</h2>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{{ $t('partner.invitees.user') }}</th>
                <th>{{ $t('partner.invitees.boundAt') }}</th>
                <th>{{ $t('partner.invitees.status') }}</th>
                <th>{{ $t('partner.invitees.netAmount') }}</th>
                <th>{{ $t('partner.invitees.commission') }}</th>
                <th>{{ $t('partner.invitees.endsAt') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="invitee in records.invitees" :key="invitee.email + invitee.boundAt">
                <td>{{ invitee.email }}</td>
                <td>{{ formatDate(invitee.boundAt) }}</td>
                <td><span class="status-tag" :class="{ 'is-good': invitee.status === 'paid' }">{{ $t(`partner.inviteeStatus.${invitee.status}`) }}</span></td>
                <td class="num">{{ money(invitee.netAmount) }}</td>
                <td class="num">{{ money(invitee.commission) }}</td>
                <td>{{ formatDate(invitee.commissionEndsAt) }}</td>
              </tr>
              <tr v-if="!records.invitees.length">
                <td colspan="6" class="empty-row">{{ $t('partner.invitees.empty') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 class="section-title">{{ $t('partner.commissions.title') }}</h2>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{{ $t('partner.commissions.date') }}</th>
                <th>{{ $t('partner.commissions.user') }}</th>
                <th>{{ $t('partner.commissions.kind') }}</th>
                <th>{{ $t('partner.commissions.netAmount') }}</th>
                <th>{{ $t('partner.commissions.commission') }}</th>
                <th>{{ $t('partner.commissions.status') }}</th>
                <th>{{ $t('partner.commissions.availableAt') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in records.commissions" :key="item.id">
                <td>{{ formatDate(item.paidAt) }}</td>
                <td>{{ item.inviteeEmail }}</td>
                <td>{{ $t(`partner.commissionKind.${item.kind}`) }}</td>
                <td class="num">{{ money(item.netAmount, item.currency) }}</td>
                <td class="num">{{ money(item.commission, item.currency) }}</td>
                <td><span class="status-tag" :class="commissionTagClass(item.status)">{{ $t(`partner.commissionStatus.${item.status}`) }}</span></td>
                <td>{{ formatDate(item.availableAt) }}</td>
              </tr>
              <tr v-if="!records.commissions.length">
                <td colspan="7" class="empty-row">{{ $t('partner.commissions.empty') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="records.payouts.length">
        <h2 class="section-title">{{ $t('partner.payouts.title') }}</h2>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{{ $t('partner.payouts.date') }}</th>
                <th>{{ $t('partner.payouts.amount') }}</th>
                <th>{{ $t('partner.payouts.method') }}</th>
                <th>{{ $t('partner.payouts.note') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payout in records.payouts" :key="payout.id">
                <td>{{ formatDate(payout.createdAt) }}</td>
                <td class="num">{{ money(payout.amount, payout.currency) }}</td>
                <td>{{ payout.method || '—' }}</td>
                <td>{{ payout.note || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div class="center-bottom">
        <section class="workspace-panel rules-panel">
          <h2 class="section-title">{{ $t('partner.rulesTitle') }}</h2>
          <ol class="rules">
            <li v-for="rule in rules" :key="rule.title" class="rule">
              <strong>{{ rule.title }}</strong>
              <span>{{ rule.desc }}</span>
            </li>
          </ol>
        </section>

        <form class="workspace-panel payout-form" @submit.prevent="submitPayoutInfo">
          <h2 class="section-title">{{ $t('partner.payoutInfo.title') }}</h2>
          <p class="share-note">{{ $t('partner.payoutInfo.desc') }}</p>
          <label class="form-field">
            <span class="field-label">{{ $t('partner.payoutInfo.method') }}</span>
            <input v-model.trim="payoutForm.method" class="text-input" :placeholder="$t('partner.payoutInfo.methodPlaceholder')" maxlength="500">
          </label>
          <label class="form-field">
            <span class="field-label">{{ $t('partner.payoutInfo.account') }}</span>
            <input v-model.trim="payoutForm.account" class="text-input" maxlength="500">
          </label>
          <label class="form-field">
            <span class="field-label">{{ $t('partner.payoutInfo.accountName') }}</span>
            <input v-model.trim="payoutForm.accountName" class="text-input" maxlength="500">
          </label>
          <button type="submit" class="secondary-btn" :disabled="isSavingPayout">{{ $t('partner.payoutInfo.save') }}</button>
        </form>
      </div>
    </template>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { useAuth } from '@/composables/auth'
import { useModals } from '@/composables/modals'
import { buildReferralLink } from '@/composables/useReferral'
import {
  applyPartner,
  fetchPartnerSummary,
  formatDate,
  formatMoney,
  savePartnerPayoutInfo
} from '@/composables/usePartner'

const APPLICATION_KEYS = ['name', 'profileUrl', 'contact']
const BENEFITS = [
  { key: 'commission', icon: 'payments' },
  { key: 'premium', icon: 'workspace_premium' },
  { key: 'audience', icon: 'redeem' }
]

export default {
  name: 'PartnerPage',
  setup() {
    const { user, authResolved } = useAuth()
    const { showLoginModal } = useModals()
    return { user, authResolved, showLoginModal }
  },
  data() {
    return {
      summary: null,
      isLoading: false,
      loadError: '',
      form: Object.fromEntries(APPLICATION_KEYS.map(key => [key, ''])),
      formError: '',
      isSubmitting: false,
      payoutForm: { method: '', account: '', accountName: '' },
      isSavingPayout: false
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale === 'zh' ? 'zh-CN' : 'en-US'
    },
    partner() {
      return this.summary?.partner
    },
    isApproved() {
      return Boolean(this.partner?.approvedAt) && ['approved', 'disabled'].includes(this.partner.status)
    },
    records() {
      return this.summary.records
    },
    referralLink() {
      return buildReferralLink(this.summary.code)
    },
    premiumLine() {
      const premium = this.partner.premium
      if (premium.granted) return this.$t('partner.premiumUntil', { date: formatDate(premium.until, this.locale) })
      if (premium.reason === 'active_paid_plan') return this.$t('partner.premiumPaidPlan')
      return this.$t('partner.premiumEnded')
    },
    benefits() {
      return BENEFITS.map(item => ({
        ...item,
        title: this.$t(`partner.benefits.${item.key}.title`),
        desc: this.$t(`partner.benefits.${item.key}.desc`)
      }))
    },
    steps() {
      return ['apply', 'share', 'earn'].map(key => ({
        title: this.$t(`partner.steps.${key}.title`),
        desc: this.$t(`partner.steps.${key}.desc`)
      }))
    },
    // 具体比例、期限和结算方式只给审核通过的伙伴看
    rules() {
      const values = {
        rate: Math.round(this.partner.commissionRate * 100),
        months: this.partner.commissionMonths,
        days: this.summary.rules.settlementDays
      }
      return ['commission', 'duration', 'fans', 'settlement', 'refund'].map(key => ({
        title: this.$t(`partner.rules.${key}.title`, values),
        desc: this.$t(`partner.rules.${key}.desc`, values)
      }))
    },
    applicationFields() {
      return APPLICATION_KEYS.map(key => ({
        key,
        label: this.$t(`partner.form.${key}`),
        placeholder: this.$t(`partner.form.${key}Placeholder`)
      }))
    }
  },
  watch: {
    user: {
      immediate: true,
      handler(value) {
        this.summary = null
        if (value) this.load()
      }
    }
  },
  methods: {
    formatDate(value) {
      return formatDate(value, this.locale)
    },
    // 还没有任何佣金时没有结算币种，金额都是 0，按美元显示
    money(amount, currency = this.records.currency || 'usd') {
      return formatMoney(amount, currency, this.locale)
    },
    commissionTagClass(status) {
      return { 'is-good': status === 'available' || status === 'paid', 'is-wait': status === 'pending' }
    },
    applySummary(summary) {
      this.summary = summary
      const payout = summary.partner?.payout
      if (payout) this.payoutForm = { method: payout.method, account: payout.account, accountName: payout.accountName }
    },
    async load() {
      this.isLoading = true
      this.loadError = ''
      try {
        this.applySummary(await fetchPartnerSummary())
      } catch (error) {
        this.loadError = this.$t('partner.loadFailed', { error: error.message })
      } finally {
        this.isLoading = false
      }
    },
    async submitApplication() {
      this.formError = ''
      this.isSubmitting = true
      try {
        this.applySummary(await applyPartner(this.form))
      } catch (error) {
        this.formError = this.$t('partner.form.failed', { error: error.message })
      } finally {
        this.isSubmitting = false
      }
    },
    async submitPayoutInfo() {
      this.isSavingPayout = true
      try {
        this.applySummary(await savePartnerPayoutInfo(this.payoutForm))
        ElMessage.success(this.$t('partner.payoutInfo.saved'))
      } catch (error) {
        ElMessage.error(this.$t('partner.payoutInfo.failed', { error: error.message }))
      } finally {
        this.isSavingPayout = false
      }
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success(this.$t('partner.copied'))
      } catch {
        ElMessage.error(this.$t('partner.copyFailed'))
      }
    }
  }
}
</script>

<style scoped>
.partner-page {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
}

/* 申请页：左边说明，右边申请卡片 */
.intro {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
  align-items: start;
  gap: var(--space-16);
  padding-top: var(--space-8);
}

.eyebrow {
  color: var(--workspace-accent-dark);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.intro-title {
  margin: var(--space-3) 0 var(--space-4);
  font-family: var(--font-family-display-zh);
  font-size: clamp(2.25rem, 4.5vw, 3.25rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.intro-lead {
  max-width: 34rem;
  margin: 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-lg);
  line-height: 1.6;
}

.benefits {
  display: flex;
  flex-direction: column;
  margin: var(--space-10) 0 0;
  padding: 0;
  list-style: none;
}

.benefit {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  padding: var(--space-5) 0;
  border-top: 1px solid var(--border-default);
}

.benefit:last-child {
  border-bottom: 1px solid var(--border-default);
}

.benefit-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: var(--surface-active);
  color: var(--workspace-accent-dark);
  font-size: 22px;
}

.benefit div {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.benefit strong {
  font-size: var(--font-size-md);
  font-weight: 600;
}

.benefit div span {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.apply-card {
  position: sticky;
  top: calc(var(--navbar-height) + var(--space-6));
  padding: var(--space-8);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  box-shadow: var(--shadow-md);
}

.card-state,
.apply-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.card-state {
  align-items: flex-start;
}

.card-loading {
  align-items: center;
  padding: var(--space-8) 0;
}

.card-title {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: 600;
}

.card-text {
  margin: 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.card-note {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.5;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.wide-btn {
  width: 100%;
  height: 44px;
}

.steps-section {
  padding-top: var(--space-8);
}

.steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-8);
  margin: var(--space-2) 0 0;
  padding: 0;
  list-style: none;
}

.step {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-4);
  border-top: 1px solid var(--border-default);
}

.step-index {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.step strong {
  font-size: var(--font-size-md);
  font-weight: 600;
}

.step span:last-child {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

/* 推广中心 */
.partner-page .workspace-header {
  margin-bottom: 0;
}

.status-panel {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin: 0;
  padding: var(--space-4) var(--space-5);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.share-panel,
.payout-form,
.rules-panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-6);
}

.payout-form .section-title,
.rules-panel .section-title {
  margin: 0;
}

.payout-form .secondary-btn {
  align-self: flex-start;
}

.share-block {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.control-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.partner-code {
  font-family: var(--font-family-data);
  font-size: var(--font-size-2xl);
  font-weight: 600;
  letter-spacing: 0.16em;
}

.link-input {
  flex: 1 1 auto;
  color: var(--ink-secondary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.share-note {
  margin: 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.center-bottom {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
  align-items: start;
  gap: var(--space-6);
}

.rules {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.rule {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-3) 0;
  border-top: 1px solid var(--border-subtle);
}

.rule strong {
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.rule span {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

@media (max-width: 900px) {
  .intro,
  .center-bottom {
    grid-template-columns: 1fr;
    gap: var(--space-8);
  }

  .apply-card {
    position: static;
  }
}

@media (max-width: 640px) {
  .intro {
    padding-top: 0;
  }

  .apply-card {
    padding: var(--space-5);
  }

  .steps {
    grid-template-columns: 1fr;
    gap: var(--space-4);
  }

  .control-row {
    flex-wrap: wrap;
  }

  .share-panel,
  .payout-form,
  .rules-panel {
    padding: var(--space-4);
  }
}
</style>
